import React, { forwardRef, useMemo, useState } from 'react';
import { Select } from 'antd';
import { AntdFormItem, AntdFormItemProps } from '@/components/AntdForm/AntdFormItem.tsx';
import { cn } from '@/utils/style.ts';
import { SelectGroupOption, SelectOption } from './antd-components.def.ts';
import { uniqBy } from 'lodash-es';

type ComboBoxInputProps = React.ComponentProps<typeof Select> & {
  options: (SelectOption | SelectGroupOption)[];
};

type AntdFormComboBoxProps = React.ComponentProps<typeof Select> & {
  options: (SelectOption | SelectGroupOption)[];
} & AntdFormItemProps;

const ComboBoxInput: React.FC<ComboBoxInputProps> = ({
  ref,
  className,
  options: rawOptions,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const selectedValue = typeof props.value === 'string' ? props.value : undefined;

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    props.onSearch?.(value);
  };

  const options = useMemo(() => {
    const customOptions = [];

    if (selectedValue && !rawOptions.map((option) => option.value).includes(selectedValue)) {
      customOptions.push({ label: selectedValue, value: selectedValue });
    }

    if (
      searchTerm &&
      !rawOptions.map((option) => option.value).includes(searchTerm) &&
      selectedValue !== searchTerm
    ) {
      customOptions.push({ label: searchTerm, value: searchTerm });
    }

    return uniqBy([...customOptions, ...rawOptions], (option) => option.value);
  }, [rawOptions, searchTerm, selectedValue]);

  return (
    <Select
      {...props}
      ref={ref}
      allowClear={props.allowClear ?? true}
      className={cn('w-full', className)}
      options={options}
      showSearch={{
        onSearch: handleSearch,
      }}
      onClear={() => {
        setSearchTerm('');
        props.onClear?.();
      }}
    />
  );
};

export const AntdFormComboBox: React.FC<AntdFormComboBoxProps> = forwardRef(
  ({ fieldProps, ...props }, ref) => {
    return (
      <AntdFormItem {...fieldProps}>
        <ComboBoxInput ref={ref} {...props} />
      </AntdFormItem>
    );
  },
);
